import {differenceInCalendarDaysLocal} from '../../../util/date';

const monthFormatter = new Intl.DateTimeFormat('en-US', {month: 'long'});

/**
 * Returns a human-readable time bucket label for a given date relative to
 * `now`.
 */
export function getTimeBucketLabel(date: Date, now: Date): string {
  const calendarDays = differenceInCalendarDaysLocal(now, date);

  if (calendarDays <= 0) {
    return 'Today';
  }
  if (calendarDays === 1) {
    return 'Yesterday';
  }
  if (calendarDays <= 7) {
    return 'Last 7 Days';
  }
  if (calendarDays <= 30) {
    return 'Last 30 Days';
  }

  const month = monthFormatter.format(date);
  if (date.getFullYear() === now.getFullYear()) {
    return month;
  }

  return `${month} ${date.getFullYear()}`;
}

/**
 * Groups a reverse-chronologically sorted array of items into time-based
 * buckets. Returns a `Map` whose keys are bucket labels and values are the
 * items in that bucket.
 *
 * Insertion order matches the input order, so iterating the map (e.g., via
 * `map`) produces groups from most recent to oldest — suitable for rendering a
 * flat list with interleaved section headers.
 *
 * **NOTE:** Input must be sorted reverse-chronologically. Unsorted input
 * may produce duplicate or misordered groups.
 */
export function groupItemsByTimeBucket<T>(
  items: readonly T[],
  getDate: (item: T) => Date,
  now: Date
): Map<string, T[]> {
  const groups = new Map<string, T[]>();

  for (const item of items) {
    const label = getTimeBucketLabel(getDate(item), now);

    let group = groups.get(label);
    if (!group) {
      group = [];
      groups.set(label, group);
    }

    group.push(item);
  }

  return groups;
}
